"use client";

import React, { useEffect, useState } from "react";

/**
 * UIX Visual Inspector Overlay – Phase 5.12
 *
 * Ctrl+I toggle: hover alapú DOM inspector, amely az összes data-uix-*
 * attribútumot megjeleníti a hovered elemen.
 *
 * Megjelenített attribútumok:
 *   - data-uix-type   → kék    – elem típusa (action, price, content, media…)
 *   - data-uix-slot   → lila   – slot azonosító (product.action.primary…)
 *   - data-uix-action → narancs – action neve (add_to_cart…)
 *   - data-uix-comp   → szürke  – komponens neve (button, badge…)
 *   - data-uix-tokens → mono lista – token debug info
 */

interface InspectorLabel {
    type:      string;
    slot?:     string;
    action?:   string;
    comp?:     string;
    tokens?:   string;
}

export const InspectorOverlay: React.FC = () => {
    if (process.env.NODE_ENV === "production") return null;

    const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
    const [label, setLabel]           = useState<InspectorLabel | null>(null);
    const [isEnabled, setIsEnabled]   = useState(false);

    // Ctrl+I toggle
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.key === 'i') {
                e.preventDefault();
                setIsEnabled(prev => !prev);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Mouse tracking
    useEffect(() => {
        if (!isEnabled) {
            setTargetRect(null);
            setLabel(null);
            return;
        }

        const handleMouseOver = (e: MouseEvent) => {
            const uixNode = (e.target as HTMLElement).closest('[data-uix-type]');
            if (uixNode) {
                setTargetRect(uixNode.getBoundingClientRect());
                setLabel({
                    type:   uixNode.getAttribute('data-uix-type')   ?? 'unknown',
                    slot:   uixNode.getAttribute('data-uix-slot')   ?? undefined,
                    action: uixNode.getAttribute('data-uix-action') ?? undefined,
                    comp:   uixNode.getAttribute('data-uix-comp')   ?? undefined,
                    tokens: uixNode.getAttribute('data-uix-tokens') ?? undefined,
                });
            } else {
                setTargetRect(null);
                setLabel(null);
            }
        };

        const handleScroll = () => { setTargetRect(null); setLabel(null); };

        document.addEventListener('mouseover', handleMouseOver);
        window.addEventListener('scroll', handleScroll, { capture: true });
        return () => {
            document.removeEventListener('mouseover', handleMouseOver);
            window.removeEventListener('scroll', handleScroll, { capture: true });
        };
    }, [isEnabled]);

    return (
        <>
            {/* Toggle gomb */}
            <div style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}>
                <button
                    onClick={() => setIsEnabled(v => !v)}
                    style={{
                        background:   isEnabled ? '#16a34a' : '#1f2937',
                        color:        '#fff',
                        border:       'none',
                        borderRadius: 6,
                        padding:      '6px 14px',
                        fontSize:     11,
                        fontWeight:   700,
                        cursor:       'pointer',
                        opacity:      isEnabled ? 1 : 0.6,
                        letterSpacing: '0.05em',
                    }}
                >
                    UIX Inspector {isEnabled ? 'ON' : 'OFF'} — Ctrl+I
                </button>
            </div>

            {/* Overlay keret */}
            {isEnabled && targetRect && (
                <div
                    style={{
                        position:      'fixed',
                        pointerEvents: 'none',
                        zIndex:        9998,
                        top:           targetRect.top,
                        left:          targetRect.left,
                        width:         targetRect.width,
                        height:        targetRect.height,
                        border:        '2px solid #3b82f6',
                        background:    'rgba(59,130,246,0.07)',
                        transition:    'all 0.08s ease-out',
                        boxSizing:     'border-box',
                    }}
                >
                    {/* Badge panel – ha az elem a viewport tetejéhez közel van, lefelé nyílik */}
                    {label && (
                        <div style={{
                            position:   'absolute',
                            ...(targetRect.top < 120
                                ? { top: '100%', marginTop: 6 }
                                : { bottom: '100%', marginBottom: 6 }
                            ),
                            left:       0,
                            display:    'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap:        4,
                        }}>
                            {/* type */}
                            <Badge color="#3b82f6" label={`type: ${label.type}`} />

                            {/* slot */}
                            {label.slot && (
                                <Badge color="#8b5cf6" label={`slot: ${label.slot}`} />
                            )}

                            {/* action */}
                            {label.action && (
                                <Badge color="#f97316" label={`action: ${label.action}`} />
                            )}

                            {/* comp */}
                            {label.comp && (
                                <Badge color="#6b7280" label={`(${label.comp})`} />
                            )}

                            {/* tokens */}
                            {label.tokens && (
                                <div style={{
                                    background:   '#111827',
                                    color:        '#d1d5db',
                                    fontSize:     10,
                                    fontFamily:   'monospace',
                                    padding:      '4px 8px',
                                    borderRadius: 4,
                                    maxWidth:     280,
                                    whiteSpace:   'pre-wrap',
                                    wordBreak:    'break-word',
                                    boxShadow:    '0 2px 8px rgba(0,0,0,0.4)',
                                }}>
                                    {label.tokens.split('; ').map((t, i) => (
                                        <div key={i}>{t}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

function Badge({ color, label }: { color: string; label: string }) {
    return (
        <span style={{
            background:    color,
            color:         '#fff',
            fontSize:      10,
            fontWeight:    700,
            padding:       '2px 8px',
            borderRadius:  4,
            whiteSpace:    'nowrap',
            boxShadow:     '0 1px 4px rgba(0,0,0,0.3)',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
        }}>
            {label}
        </span>
    );
}
